import React from "react";
import { useSelector } from "react-redux";
import * as steps from "common/steps";
import { getActiveStep, getSubmissionPanelLoading } from "common/selectors";
import Intro from "intro/components/Intro";
import Terms from "terms/components/Terms";
import Upload from "upload/components/Upload";
import BasicDetails from "basic-details/components/BasicDetails";
import FileDetails from "file-details/components/FileDetails";
import LoadingPanel from "general-components/LoadingPanel";
import CancelSubmissionDialog from "./CancelSubmissionDialog";
import InvalidSubmissionDialog from "./InvalidSubmissionDialog";
import SubmissionErrorDialog from "./SubmissionErrorDialog";
import SubmissionPanelHeader from "./SubmissionPanelHeader";
import SubmissionPanelMenu from "./SubmissionPanelMenu";
import UploadDialog from "./UploadDialog";
import ValidationErrorsDialog from "./ValidationErrorsDialog";

import backgroundImage from "submission/components/bg-Latop-STIP-blur.png";
import biblioboardLogo from "submission/components/icon-BiblioBoard-white.png";
import HelpIcon from "@material-ui/icons/Help";

import Drawer from "@material-ui/core/Drawer";
import Hidden from "@material-ui/core/Hidden";

import { makeStyles } from "@material-ui/core/styles";
import { FormControl, NativeSelect, Typography } from "@material-ui/core";
import Link from "@material-ui/core/Link";
import { useTranslation } from "react-i18next";
import { useIsQatarProject } from "../../common/projectUtils";

const getStep = step => {
  switch (step) {
    case steps.INTRO:
      return <Intro />;

    case steps.TERMS:
      return <Terms />;

    case steps.FILE_UPLOAD:
      return <Upload />;

    case steps.BASIC_DETAILS:
      return <BasicDetails />;

    case steps.FILE_DETAILS:
      return <FileDetails />;

    default:
      return <h1>Unknown Step</h1>;
  }
};

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
  },

  content: {
    flexGrow: 1,
    height: 800,
    display: "flex",
    flexDirection: "column",

    [theme.breakpoints.up("sm")]: {
      marginRight: 50,
      marginLeft: 50,
    },
  },

  contentBody: {
    display: "flex",
    flexDirection: "column",
  },

  drawer: {
    [theme.breakpoints.up("sm")]: {
      height: "100%",
      width: 150,
      flexShrink: 0,
    },
  },

  drawerPaper: {
    width: 150,
    minHeight: 400,
    height: "100%",
    backgroundImage: `url(${backgroundImage})`,
    backgroundSize: "cover",
  },

  languageIcon: {
    color: theme.palette.secondary.light,
  },

  languageOption: {
    color: theme.palette.primary.dark,
  },

  languageSelect: {
    color: theme.palette.secondary.light,

    "&:focus": {
      borderWidth: 2,
      borderColor: "white",
      borderStyle: "solid",
    },
  },

  languageSelectControl: {
    marginTop: theme.spacing(1),
  },

  logo: {
    textAlign: "center",
    marginTop: theme.spacing(3),
  },

  sidebar: {
    height: "100%",
    background: `${theme.palette.secondary.main} 0% 0% no-repeat padding-box`,
    opacity: 0.8,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    paddingTop: theme.spacing(5),
    paddingBottom: theme.spacing(5),
  },

  supportLink: {
    textAlign: "center",
    marginBottom: theme.spacing(2),
  },
}));

const SubmissionPanel = () => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const activeStep = useSelector(getActiveStep);
  const loading = useSelector(getSubmissionPanelLoading);
  const isQatarProject = useIsQatarProject();

  return loading ? (
    <LoadingPanel message={t("submission_loading")} />
  ) : (
    <main className={classes.root}>
      <div className={classes.drawer} aria-label="info support">
        <Hidden xsDown implementation="css">
          <Drawer
            classes={{
              paper: classes.drawerPaper,
            }}
            variant="permanent"
            open
            anchor={i18n.dir() === "rtl" ? "right" : "left"}
          >
            <div className={classes.sidebar}>
              <div className={classes.logo}>
                <div>
                  <img
                    src={biblioboardLogo}
                    width="50px"
                    height="36px"
                    alt="BibloBoard"
                  />
                </div>

                <FormControl className={classes.languageSelectControl}>
                  <NativeSelect
                    disableUnderline
                    value={i18n.language}
                    onChange={event => i18n.changeLanguage(event.target.value)}
                    inputProps={{
                      "aria-label": "select language",
                      name: "userLanguage",
                    }}
                    classes={{
                      select: classes.languageSelect,
                      icon: classes.languageIcon,
                    }}
                  >
                    {/* Only show English and Arabic if Qatar project */}
                    <option className={classes.languageOption} value="en">
                      English
                    </option>
                    {!isQatarProject && (
                      <option className={classes.languageOption} value="es">
                        Español
                      </option>
                    )}
                    <option className={classes.languageOption} value="ar">
                      عربي (Arabic)
                    </option>
                  </NativeSelect>
                </FormControl>
              </div>

              <div className={classes.supportLink}>
                <Link
                  color="textSecondary"
                  underline="none"
                  href="mailTo:support@biblioboard.com"
                >
                  <HelpIcon className={classes.helpIcon} />
                  <Typography variant="body1" color="textSecondary">
                    {t("app_support")}
                  </Typography>
                </Link>
              </div>
            </div>
          </Drawer>
        </Hidden>
      </div>
      <div className={classes.content}>
        <SubmissionPanelHeader />
        <div className={classes.contentBody}>
          {activeStep !== steps.INTRO && <SubmissionPanelMenu />}
          {getStep(activeStep)}
        </div>
      </div>

      <CancelSubmissionDialog />
      <InvalidSubmissionDialog />
      <UploadDialog />
      <ValidationErrorsDialog />
      <SubmissionErrorDialog />
    </main>
  );
};

export default SubmissionPanel;
